import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Text,
  TextInput,
  LoadingOverlay,
  Tooltip,
  Pagination,
  Select,
  Stack,
  Flex,
  Card,
  Menu,
  rem,
  Progress,
  Indicator,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconSearch,
  IconCamera,
  IconMapPin,
  IconWifi,
  IconWifiOff,
  IconDots,
  IconTrash,
  IconSettings,
  IconPlayerPlay,
  IconPlayerPause,
  IconTestPipe,
  IconActivity,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useCameras, useToggleCameraStatus, useTestCameraConnection } from '../../hooks/useCameras';
import { useAreas } from '../../hooks/useAreas';
import type { Camera } from '../../types/api';

const Cameras: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState('25');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [areaFilter, setAreaFilter] = useState<string>('');

  const {
    data: camerasData,
    isLoading,
    error,
  } = useCameras({
    page,
    per_page: parseInt(perPage),
    search: debouncedSearch,
    sort_by: sortBy,
    sort_order: sortOrder,
    filters: {
      ...(statusFilter && { status: statusFilter }),
      ...(areaFilter && { area_id: areaFilter }),
    },
  });

  const { data: areasData } = useAreas({ per_page: 100 });

  const toggleCameraStatusMutation = useToggleCameraStatus();
  const testConnectionMutation = useTestCameraConnection();

  const cameras = camerasData?.cameras || [];
  const pagination = camerasData?.pagination;
  const areas = areasData?.areas || [];

  const handleCreateCamera = () => {
    navigate('/admin/cameras/new');
  };

  const handleEditCamera = (camera: Camera) => {
    navigate(`/admin/cameras/${camera.id}/edit`);
  };

  const handleViewCamera = (camera: Camera) => {
    navigate(`/admin/cameras/${camera.id}`);
  };

  const handleToggleStatus = async (camera: Camera) => {
    try {
      await toggleCameraStatusMutation.mutateAsync(camera.id);
    } catch (error) {
      // Error handling: Failed to toggle camera status
    }
  };

  const handleTestConnection = async (camera: Camera) => {
    try {
      await testConnectionMutation.mutateAsync(camera.id);
    } catch (error) {
      // Error handling: Failed to test camera connection
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'red',
      error: 'orange',
      maintenance: 'blue',
    };
    return colors[status] || 'gray';
  };

  const getHealthStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      healthy: 'green',
      warning: 'yellow',
      critical: 'red',
    };
    return colors[status] || 'gray';
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatLastActivity = (lastActivity: string | undefined) => {
    if (!lastActivity) return 'Never';
    const date = new Date(lastActivity);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (error) {
    notifications.show({
      title: 'Error',
      message: 'Failed to load cameras. Please try again.',
      color: 'red',
    });
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Camera Management</Title>
          <Text size="sm" c="dimmed">
            Monitor and manage security cameras across all areas
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleCreateCamera}
        >
          Add Camera
        </Button>
      </Group>

      {/* Filters and Search */}
      <Card>
        <Stack gap="md">
          <Group>
            <TextInput
              placeholder="Search cameras..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flexGrow: 1, maxWidth: 400 }}
            />
            <Select
              placeholder="Filter by area"
              data={[
                { value: '', label: 'All Areas' },
                ...areas
                  .filter((area: any) => area && area.name)
                  .map((area: any) => ({
                    value: area.id.toString(),
                    label: area.name,
                  })),
              ]}
              value={areaFilter}
              onChange={(value) => setAreaFilter(value || '')}
              style={{ minWidth: 200 }}
            />
            <Select
              placeholder="Filter by status"
              data={[
                { value: '', label: 'All Status' },
                { value: 'online', label: 'Online' },
                { value: 'offline', label: 'Offline' },
                { value: 'error', label: 'Error' },
                { value: 'maintenance', label: 'Maintenance' },
              ]}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || '')}
              style={{ minWidth: 150 }}
            />
          </Group>

          <Group justify="flex-end">
            <Select
              label="Per page"
              value={perPage}
              onChange={(value) => {
                setPerPage(value || '25');
                setPage(1);
              }}
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              style={{ width: 100 }}
            />
          </Group>
        </Stack>
      </Card>

      {/* Cameras Table */}
      <Paper withBorder>
        <LoadingOverlay visible={isLoading} />

        {cameras.length === 0 && !isLoading ? (
          <Flex direction="column" align="center" justify="center" py="xl">
            <IconCamera size={48} color="gray" />
            <Title order={4} c="dimmed" mt="md">
              No cameras found
            </Title>
            <Text size="sm" c="dimmed" ta="center" mt="xs">
              {search || statusFilter || areaFilter
                ? 'Try adjusting your search criteria'
                : 'Get started by adding your first camera'}
            </Text>
            {!search && !statusFilter && !areaFilter && (
              <Button
                variant="outline"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateCamera}
                mt="md"
              >
                Add Camera
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    Camera {getSortIcon('name')}
                  </Table.Th>
                  <Table.Th>Location & Area</Table.Th>
                  <Table.Th>Status & Health</Table.Th>
                  <Table.Th>Stream Info</Table.Th>
                  <Table.Th>Activity</Table.Th>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('last_activity_at')}
                  >
                    Last Activity {getSortIcon('last_activity_at')}
                  </Table.Th>
                  <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {cameras.map((camera: any) => (
                  <Table.Tr key={camera.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Indicator
                          color={getStatusColor(camera.status)}
                          position="bottom-end"
                          size={12}
                          processing={camera.status === 'online'}
                        >
                          <IconCamera size={24} />
                        </Indicator>
                        <div>
                          <Text fw={500} size="sm">
                            {camera.name}
                          </Text>
                          {camera.description && (
                            <Text size="xs" c="dimmed">
                              {camera.description}
                            </Text>
                          )}
                          <Text size="xs" c="dimmed">
                            ID: {camera.id}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={4}>
                          <IconMapPin size={12} />
                          <Text size="xs">{camera.location?.area?.name || 'No area assigned'}</Text>
                        </Group>
                        {camera.location?.area && (
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {camera.location.area.area_type}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Badge
                            color={getStatusColor(camera.status)}
                            variant="light"
                            size="xs"
                            leftSection={
                              camera.status === 'online' ?
                                <IconWifi size={10} /> :
                                <IconWifiOff size={10} />
                            }
                          >
                            {camera.status}
                          </Badge>
                          <Badge
                            color={getHealthStatusColor(camera.health_status)}
                            variant="light"
                            size="xs"
                          >
                            {camera.health_status}
                          </Badge>
                        </Group>
                        {camera.health_status !== 'healthy' && (
                          <Group gap={4}>
                            <IconAlertTriangle size={12} color="orange" />
                            <Text size="xs" c="orange">Needs attention</Text>
                          </Group>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="xs">{camera.resolution}</Text>
                        <Text size="xs" c="dimmed">{camera.fps} FPS</Text>
                        {camera.technical_specs?.stream_quality?.value && (
                          <Text size="xs" c="dimmed">
                            Quality: {camera.technical_specs.stream_quality.value}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={4}>
                          <IconActivity size={12} />
                          <Text size="xs">{camera.activity_stats?.activities_24h || 0} detections</Text>
                        </Group>
                        <Progress
                          value={Math.min((camera.activity_stats?.activities_24h || 0) / 10 * 100, 100)}
                          size="xs"
                          color={(camera.activity_stats?.activities_24h || 0) > 50 ? 'red' : 'blue'}
                        />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {formatLastActivity(camera.activity_stats?.last_activity)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="View details">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleViewCamera(camera)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Edit camera">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleEditCamera(camera)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconTestPipe style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => handleTestConnection(camera)}
                              disabled={testConnectionMutation.isPending}
                            >
                              Test Connection
                            </Menu.Item>
                            <Menu.Item
                              leftSection={
                                camera.is_active ?
                                  <IconPlayerPause style={{ width: rem(14), height: rem(14) }} /> :
                                  <IconPlayerPlay style={{ width: rem(14), height: rem(14) }} />
                              }
                              onClick={() => handleToggleStatus(camera)}
                              disabled={toggleCameraStatusMutation.isPending}
                            >
                              {camera.is_active ? 'Disable' : 'Enable'} Camera
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconSettings style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => navigate(`/admin/cameras/${camera.id}/settings`)}
                            >
                              Camera Settings
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                              color="red"
                            >
                              Delete Camera
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={pagination.total_pages}
                  size="sm"
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      {/* Summary Stats */}
      {pagination && (
        <Text size="sm" c="dimmed" ta="center">
          Showing {cameras.length} of {pagination.total_count} cameras
          {(search || statusFilter || areaFilter) && ' (filtered)'}
        </Text>
      )}
    </Stack>
  );
};

export default Cameras;
